import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { tap } from "rxjs";
import { LoaderService } from "./loader.service";
import {PagedFilter, CompetitorFilter, FindEventsRequest, FindUsersDetailsRequest, RequestFilter, Race} from "@model";
import { FindOrganisersRequest } from "../model/find-organisers-request";
import { EnvironmentService } from "./environment.service";
import { DOCUMENT } from "@angular/common";
import {TranslocoService} from "@ngneat/transloco";
import {getLanguageValue} from "@utility";

@Injectable({ providedIn: 'root' })
export class ExportService {
  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private translocoService: TranslocoService,
    private environmentService: EnvironmentService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  private downloadFile(url: string, type: string, fileName: string, filter?: any, appendDate: boolean = true) {
    this.loaderService.startLoading();
    this.http.post<any>(this.environmentService.getApiUrl() + url, filter, { responseType: 'blob' as 'json' }).pipe(
      tap(() => this.loaderService.dismissLoading())
    ).subscribe(
      (blob: Blob) => this.createFile(type, blob, fileName, appendDate),
      error => console.error('Error downloading file:', error)
    );
  }

  downloadListOfEvents(type: string, filter: FindEventsRequest): void {
    this.downloadFile(`/api/export/events/${type}`, type, "list_of_events", filter);
  }

  downloadListOfRaces(type: string, eventId: number): void {
    this.downloadFile(`/api/export/event/races/${type}`, type, "list_of_races", eventId);
  }

  downloadStartList(eventId: number, raceId?: number): void {
    const url = raceId ? `/api/export/event/startlist/pdf?raceId=${raceId}&eventId=${eventId}` : `/api/export/event/startlist/pdf?eventId=${eventId}`;
    this.downloadFile(url, "pdf", "race_start_list");
  }

  downloadCompetitorsListForRace(type: string, filter: PagedFilter<CompetitorFilter>, race: Race): void {
    const fileName = `${race.id}-${getLanguageValue(race.name, race.nameEng, race.nameIta, race.nameSlo, race.nameSr, race.nameBs, this.translocoService.getActiveLang(), race.name)}`;
    this.downloadFile(`/api/export/race/competitorlist/${type}`, type,  fileName, filter, false);
  }

  downloadCompetitorsListForEvent(type: string, filter?: PagedFilter<CompetitorFilter>): void {
    this.downloadFile(`/api/export/event/competitorlist/${type}`, type, "competitors_for_event", filter);
  }

  downloadResultsForRace(type: string, filter?: PagedFilter<CompetitorFilter>): void {
    this.downloadFile(`/api/export/race/results/${type}`, type, "results_for_race", filter);
  }

  downloadLeagues(type: string): void {
    this.downloadFile(`/api/export/leagues/${type}`, type, "leagues", undefined);
  }


  downloadUsersByRole(type: string, filter?: FindUsersDetailsRequest): void {
    this.downloadFile(`/api/export/users/${type}`, type, "user_by_role", filter);
  }

  downloadOrganisers(type: string): void {
    this.downloadFile(`/api/export/organisers/${type}`, type, "organisers");
  }

  downloadAdmins(type: string, filter: FindOrganisersRequest): void {
    this.downloadFile(`/api/export/administrators/${type}`, type, "admins", filter);
  }

  downloadRaceRegistrationQRCode(eventName?: string, eventId?: number): void {
    this.downloadFile(`/api/public/export/event/${eventId}/registration-qr.pdf`, "pdf", eventName + "_registration_qr_code", eventId);
  }

  downloadInvoice(orderId: number): void {
    this.downloadFile(`/api/export/invoice/${orderId}`, "pdf",  "invoice");
  }

  private createFile(fileType: string, blob: Blob, fileName: string, appendDate: boolean = true) {
    const formattedDateTime = this.getCurrentDateTimeFormatted();
    const filename = `${appendDate ? `${fileName}_${formattedDateTime}` : fileName}.${fileType}`;
    const downloadLink = this.document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.setAttribute('download', filename);
    this.document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  private getCurrentDateTimeFormatted(): string {
    const currentDateTime = new Date();
    const year = currentDateTime.getFullYear();
    const month = this.padNumber(currentDateTime.getMonth() + 1, 2);
    const day = this.padNumber(currentDateTime.getDate(), 2);
    return `${year}-${month}-${day}`;
  }

  private padNumber(num: number, size: number): string {
    return num.toString().padStart(size, '0');
  }
}
